import { useState } from 'react';
import { Plus, Users } from 'lucide-react';
import { useJobs, useJobCandidates } from '@/api/queries/recruitment.queries';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar } from '@/components/shared/AvatarWithInitials';
import { cn } from '@/lib/cn';
import { usePlan } from '@/hooks/usePlan';
import { UpgradePrompt } from '@/components/shared/UpgradePrompt';

const STAGES = ['APPLIED','SCREENED','INTERVIEW','OFFERED','HIRED','REJECTED'];
const STAGE_COLORS = { APPLIED:'blue', SCREENED:'purple', INTERVIEW:'yellow', OFFERED:'orange', HIRED:'green', REJECTED:'red' };

export default function RecruitmentPage() {
  const { hasFeature } = usePlan();
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [view, setView] = useState('kanban');

  const { data: jobsResp } = useJobs();
  const jobs = jobsResp?.data || [];

  const effectiveJobId = selectedJobId || jobs[0]?.id;
  const selectedJob = jobs.find(j => j.id === effectiveJobId);

  const { data: candidatesResp } = useJobCandidates(effectiveJobId);
  const candidates = candidatesResp?.data || [];

  if (!hasFeature('recruitment')) {
    return <UpgradePrompt feature="Recruitment ATS" requiredPlan="Pro" description="Post jobs, manage candidates, and run your hiring pipeline with the Pro plan."/>;
  }

  return (
    <div className="space-y-5 fade-in">
      <PageHeader
        title="Recruitment"
        subtitle={`${jobs.filter(j=>j.status==='OPEN').length} open jobs`}
        actions={<Button size="sm" icon={Plus} onClick={() => window.location.href='/recruitment/new'}>Post Job</Button>}
      />

      <div className="flex items-center gap-2 px-4 py-2.5 rounded-[10px] bg-blue-50 dark:bg-blue-900/20 border border-blue-200 text-[12px] text-blue-700 dark:text-blue-300">
        <span className="font-semibold">Info:</span>
        <span>Recruitment data is live from the service. Email notifications to candidates available in Phase 2.</span>
      </div>

      {/* Jobs list */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {jobs.map(job=>(
          <div key={job.id}
            onClick={()=>setSelectedJobId(job.id)}
            className={cn('card cursor-pointer transition-all border-2 p-5', effectiveJobId===job.id ? 'border-[var(--accent)]' : 'border-transparent')}>
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-[14px] font-semibold text-[var(--text-primary)]">{job.title}</h3>
              <StatusBadge status={job.status}/>
            </div>
            <p className="text-[12px] text-[var(--text-secondary)]">{job.department?.name||job.department} · {job.location}</p>
            <p className="text-[12px] text-[var(--text-tertiary)] mt-1">{job.salary_range}</p>
            <div className="flex items-center gap-2 mt-3">
              <Users size={12} className="text-[var(--text-tertiary)]"/>
              <span className="text-[12px] text-[var(--text-secondary)]">{job.applications} applicants</span>
            </div>
          </div>
        ))}
      </div>

      {selectedJob && (
        <>
          <div className="flex items-center justify-between">
            <h2 className="text-[16px] font-bold text-[var(--text-primary)]">Pipeline — {selectedJob.title}</h2>
            <div className="flex gap-1 p-1 rounded-[8px] bg-[var(--bg-muted)]">
              {['kanban','list'].map(v=>(
                <button key={v} onClick={()=>setView(v)}
                  className={cn('px-3 py-1 rounded-[6px] text-[12px] font-semibold capitalize transition-colors',
                    view===v ? 'bg-white shadow-sm text-[var(--text-primary)]' : 'text-[var(--text-secondary)]'
                  )}>{v}</button>
              ))}
            </div>
          </div>

          {view==='kanban' ? (
            <div className="overflow-x-auto pb-4">
              <div className="flex gap-3 min-w-max">
                {STAGES.filter(s=>s!=='REJECTED').map(stage=>{
                  const stageCands = candidates.filter(c=>c.stage===stage);
                  return (
                    <div key={stage} className="w-[220px]">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant={STAGE_COLORS[stage]} size="sm">{stage}</Badge>
                        <span className="text-[11px] text-[var(--text-tertiary)]">{stageCands.length}</span>
                      </div>
                      <div className="space-y-2">
                        {stageCands.map(c=>(
                          <div key={c.id} className="card p-3 space-y-2">
                            <div className="flex items-center gap-2">
                              <Avatar name={c.name} size="sm"/>
                              <div className="flex-1 min-w-0">
                                <p className="text-[12px] font-semibold text-[var(--text-primary)] truncate">{c.name}</p>
                                <p className="text-[10px] text-[var(--text-tertiary)]">{c.experience}</p>
                              </div>
                            </div>
                            {c.notes && <p className="text-[11px] text-[var(--text-secondary)] leading-snug">{c.notes}</p>}
                          </div>
                        ))}
                        {stageCands.length===0 && (
                          <div className="h-16 border-2 border-dashed border-[var(--border)] rounded-[8px] flex items-center justify-center">
                            <span className="text-[11px] text-[var(--text-tertiary)]">No candidates</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="card p-0 overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[var(--border)]">
                    {['Candidate','Experience','Stage','Notes','Applied On'].map(h=>(
                      <th key={h} className="text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--text-tertiary)] px-5 py-3">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {candidates.map(c=>(
                    <tr key={c.id} className="hover:bg-[var(--bg-muted)] transition-colors">
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <Avatar name={c.name} size="sm"/>
                          <div>
                            <p className="text-[13px] font-semibold text-[var(--text-primary)]">{c.name}</p>
                            <p className="text-[11px] text-[var(--text-tertiary)]">{c.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 text-[13px] text-[var(--text-secondary)]">{c.experience}</td>
                      <td className="px-5 py-3.5"><Badge variant={STAGE_COLORS[c.stage]||'default'} size="sm">{c.stage}</Badge></td>
                      <td className="px-5 py-3.5 text-[12px] text-[var(--text-secondary)] max-w-[200px] truncate">{c.notes}</td>
                      <td className="px-5 py-3.5 text-[12px] text-[var(--text-tertiary)]">{c.applied_on}</td>
                    </tr>
                  ))}
                  {candidates.length===0 && <tr><td colSpan={5} className="px-5 py-12 text-center text-[14px] text-[var(--text-tertiary)]">No candidates yet</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
